import React, { useRef } from "react";
import { FaGripVertical, FaThumbtack, FaTimes, FaTrash } from "react-icons/fa";

import { NOTE_STYLES, NOTE_FONT } from "./canvas/constants";

function relativeTime(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * The free-drag glass-fridge corkboard. Below the md breakpoint it falls back
 * to a stacked grid, since free positioning isn't practical on a phone.
 */
function NotesBoard({
  board,
  editable,
  isMobile,
  dark,
  openPalette,
  setOpenPalette,
}) {
  const { visible, notes, loading, patchLocal, patchNote, deleteNote, addTag, removeTag, setTagFilter } =
    board;

  const dragRef = useRef(null);

  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    d.lastX = Math.max(0, d.origX + (e.clientX - d.startX));
    d.lastY = Math.max(0, d.origY + (e.clientY - d.startY));
    d.moved = true;
    patchLocal(d.id, { x: d.lastX, y: d.lastY });
  };

  const onDragEnd = () => {
    const d = dragRef.current;
    if (!d) return;
    document.removeEventListener("pointermove", onDragMove);
    document.removeEventListener("pointerup", onDragEnd);
    if (d.moved) patchNote(d.id, { x: d.lastX, y: d.lastY });
    dragRef.current = null;
  };

  const startDrag = (e, note) => {
    if (!editable || isMobile) return;
    e.preventDefault();
    dragRef.current = {
      id: note._id,
      startX: e.clientX,
      startY: e.clientY,
      origX: note.x,
      origY: note.y,
      lastX: note.x,
      lastY: note.y,
      moved: false,
    };
    document.addEventListener("pointermove", onDragMove);
    document.addEventListener("pointerup", onDragEnd);
  };

  const boardBg = dark
    ? "linear-gradient(100deg,#3a4658 0%,#1e293b 45%,#0f172a 100%)"
    : "linear-gradient(100deg,#aabfd6 0%,#8aa6c4 45%,#9db4cf 100%)";
  const sheen = dark
    ? "linear-gradient(115deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0.035) 54%, rgba(255,255,255,0) 72%)"
    : "linear-gradient(115deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 26%, rgba(255,255,255,0.12) 52%, rgba(255,255,255,0) 68%)";
  const dividerColor = dark ? "rgba(0,0,0,0.45)" : "rgba(15,23,42,0.16)";

  return (
    <div
      className="notes-board"
      style={{
        position: "relative",
        background: boardBg,
        borderRadius: "14px",
        padding: isMobile ? "22px 14px" : "10px",
        minHeight: isMobile ? "auto" : "560px",
        overflow: "auto",
        boxShadow:
          "inset 0 2px 30px rgba(255,255,255,0.25), inset 0 -20px 40px rgba(15,23,42,0.15), 0 22px 45px rgba(15,23,42,0.3)",
        border: "1px solid rgba(255,255,255,0.35)",
        ...(isMobile
          ? {
              display: "flex",
              flexWrap: "wrap",
              gap: "20px",
              justifyContent: "center",
              alignItems: "flex-start",
            }
          : {}),
      }}
    >
      {/* Glass reflections */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "14px",
          background: sheen,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      {/* Centre divider between the two doors */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "50%",
          width: "2px",
          transform: "translateX(-1px)",
          background: dividerColor,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      {/* Two vertical door handles */}
      <div
        style={{
          position: "absolute",
          top: "30%",
          height: "38%",
          left: "calc(50% - 17px)",
          width: "7px",
          borderRadius: "6px",
          background: "linear-gradient(90deg,#cbd5e1,#f8fafc 45%,#94a3b8)",
          boxShadow: "0 4px 10px rgba(0,0,0,0.35)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "30%",
          height: "38%",
          left: "calc(50% + 10px)",
          width: "7px",
          borderRadius: "6px",
          background: "linear-gradient(90deg,#94a3b8,#f8fafc 55%,#cbd5e1)",
          boxShadow: "0 4px 10px rgba(0,0,0,0.35)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {loading && (
        <p
          style={{
            color: "#fff",
            padding: "40px",
            fontStyle: "italic",
            position: "relative",
            zIndex: 1,
          }}
        >
          Loading notes…
        </p>
      )}

      {!loading && visible.length === 0 && (
        <p
          style={{
            color: "#ffffffcc",
            padding: "40px",
            fontStyle: "italic",
            position: "relative",
            zIndex: 1,
          }}
        >
          {notes.length === 0
            ? editable
              ? "The board is empty — add your first note."
              : "No notes on the board yet."
            : "No notes match your search."}
        </p>
      )}

      {visible.map((note) => {
        const style = NOTE_STYLES[note.color] || NOTE_STYLES[0];
        return (
          <div
            key={note._id}
            style={{
              position: isMobile ? "relative" : "absolute",
              ...(isMobile
                ? { width: "min(100%, 300px)" }
                : { left: `${note.x}px`, top: `${note.y}px`, width: "200px" }),
              background: style.paper,
              borderRadius: "4px",
              boxShadow: note.pinned
                ? "0 0 0 3px #ff6b35, 0 10px 20px rgba(15,23,42,0.3)"
                : "0 10px 18px rgba(15,23,42,0.28)",
              zIndex: note.pinned ? 5 : 1,
            }}
          >
            {/* Glossy magnet pin (also the colour control) */}
            <button
              onClick={() =>
                editable && setOpenPalette(openPalette === note._id ? null : note._id)
              }
              title={editable ? "Change colour" : ""}
              disabled={!editable}
              style={{
                position: "absolute",
                top: "-11px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                border: "none",
                background: `radial-gradient(circle at 33% 28%, rgba(255,255,255,0.95), ${style.pin} 72%)`,
                boxShadow: "0 3px 5px rgba(0,0,0,0.4), inset 0 -2px 3px rgba(0,0,0,0.25)",
                cursor: editable ? "pointer" : "default",
                zIndex: 4,
              }}
            />

            {/* Header / drag handle */}
            <div
              onPointerDown={(e) => startDrag(e, note)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "13px 8px 6px",
                cursor: editable && !isMobile ? "grab" : "default",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                touchAction: editable && !isMobile ? "none" : "auto",
              }}
            >
              <span style={{ color: "#00000033" }}>
                {editable && !isMobile && <FaGripVertical />}
              </span>

              <div className="d-flex align-items-center gap-2">
                {note.pinned && !editable && (
                  <FaThumbtack style={{ color: "#ff6b35", fontSize: "0.8rem" }} />
                )}
                {editable && (
                  <FaThumbtack
                    role="button"
                    title={note.pinned ? "Unpin" : "Pin"}
                    onClick={() => patchNote(note._id, { pinned: !note.pinned })}
                    style={{
                      color: note.pinned ? "#ff6b35" : "#00000055",
                      fontSize: "0.8rem",
                      cursor: "pointer",
                    }}
                  />
                )}
              </div>
            </div>

            {/* Colour palette popover */}
            {openPalette === note._id && editable && (
              <div
                className="d-flex flex-wrap gap-1 p-2"
                style={{
                  background: "rgba(255,255,255,0.6)",
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                {NOTE_STYLES.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      patchNote(note._id, { color: i });
                      setOpenPalette(null);
                    }}
                    style={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      border: note.color === i ? "2px solid #0f172a" : "2px solid #fff",
                      background: s.paper,
                      cursor: "pointer",
                    }}
                  />
                ))}
              </div>
            )}

            {/* Body */}
            <div style={{ padding: "8px 10px 10px" }}>
              <textarea
                value={note.text}
                readOnly={!editable}
                onChange={(e) => patchLocal(note._id, { text: e.target.value })}
                onBlur={(e) => editable && patchNote(note._id, { text: e.target.value })}
                placeholder={editable ? "Write a note…" : ""}
                style={{
                  width: "100%",
                  height: "96px",
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  resize: "none",
                  color: "#1f2937",
                  fontSize: "0.9rem",
                  lineHeight: "1.5",
                  fontFamily: NOTE_FONT,
                }}
              />

              {/* Tags */}
              <div className="d-flex flex-wrap gap-1 mt-1">
                {(note.tags || []).map((t) => (
                  <span
                    key={t}
                    onClick={() => setTagFilter(t)}
                    style={{
                      background: "rgba(0,0,0,0.08)",
                      borderRadius: "10px",
                      padding: "1px 7px",
                      fontSize: "0.68rem",
                      color: "#334155",
                      cursor: "pointer",
                    }}
                  >
                    #{t}
                    {editable && (
                      <FaTimes
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTag(note, t);
                        }}
                        style={{ marginLeft: 4, fontSize: "0.6rem" }}
                      />
                    )}
                  </span>
                ))}
              </div>

              {editable && (
                <input
                  placeholder="+ tag"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      addTag(note, e.target.value);
                      e.target.value = "";
                    }
                  }}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: "0.7rem",
                    width: "60px",
                    marginTop: "2px",
                    color: "#475569",
                  }}
                />
              )}

              {/* Footer: owner + time + actions */}
              <div
                className="d-flex align-items-center justify-content-between mt-2"
                style={{ fontSize: "0.62rem", color: "#00000077" }}
              >
                <span>
                  {!editable && note.ownerName
                    ? `by ${note.ownerName}`
                    : `Updated ${relativeTime(note.updatedAt)}`}
                </span>

                {editable && (
                  <FaTrash
                    role="button"
                    title="Delete"
                    onClick={() => deleteNote(note._id)}
                    style={{ color: "#00000055", cursor: "pointer" }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default NotesBoard;
